/**
 * KPLC prepaid tokens: buying them in a batch, and getting them to the person
 * whose meter they belong to.
 *
 * ## The problem this exists for
 *
 * GoSpeedNet's equipment sits in other people's buildings — a FAT on a
 * landlord's wall, a switch in a shop's back room — and the power it draws is
 * on that landlord's prepaid meter. So every month somebody buys a token for
 * each of them and reads a twenty-digit number down a phone. Forty meters is
 * forty M-Pesa payments and forty phone calls, and the failure mode is not
 * dramatic: one gets missed, a meter runs out at two in the morning, and a
 * FAT box goes dark taking a street of customers with it.
 *
 * ## What is arithmetic and what is somebody else's system
 *
 * Everything in this file is arithmetic and validation — meter numbers, token
 * shapes, what a batch costs, which meters are due. None of it talks to
 * anybody.
 *
 * The two things that leave this building are deliberately not modelled as if
 * they were reliable: **paying** goes through Safaricom and **the token comes
 * back by SMS from KPLC**, neither of which is a function call. A purchase is
 * therefore a row with a state, not a return value, and the state machine
 * below is what makes a half-finished batch recoverable rather than a mystery.
 */
import { fillSmsTemplate } from './sms-templates.js';
import { formatCents } from './money.js';
import { toE164 } from './phone.js';
/* ── Meters ──────────────────────────────────────────────────────────────── */
/**
 * A KPLC meter number is 8 to 13 digits: most are eleven, newer ones thirteen,
 * and old token meters still on the walls of older blocks have eight.
 *
 * Checked because the number is typed off a meter in somebody's stairwell,
 * often photographed badly, and a wrong one does not fail — it buys a token
 * for a stranger's meter. That money is gone and nobody finds out until the
 * landlord rings to say the power is off.
 *
 * The leading digit is not validated. KPLC has issued several ranges over the
 * years and a rule that rejected a real meter would be worse than one that
 * accepts a fake.
 */
export const METER_DIGITS_MIN = 8;
export const METER_DIGITS_MAX = 13;
/** "8 to 13", for messages. */
export const METER_DIGITS_RANGE = `${METER_DIGITS_MIN} to ${METER_DIGITS_MAX}`;
export function normaliseMeterNumber(value) {
    return value.replace(/\D/g, '');
}
export function refuseMeterNumber(value) {
    const digits = normaliseMeterNumber(value);
    if (digits.length === 0)
        return 'Enter the meter number from the meter itself.';
    if (digits.length < METER_DIGITS_MIN || digits.length > METER_DIGITS_MAX) {
        return `A KPLC meter number is ${METER_DIGITS_RANGE} digits and that is ${digits.length}. Check it against the meter — buying against the wrong number pays for somebody else's power.`;
    }
    return null;
}
/** "12345678901" as "1234 5678 901", which is how it is read aloud. */
export function formatMeterNumber(value) {
    const digits = normaliseMeterNumber(value);
    return digits.replace(/(\d{4})(\d{4})(\d+)/, '$1 $2 $3');
}
/* ── Tokens ──────────────────────────────────────────────────────────────── */
/**
 * A prepaid token is twenty digits, entered on the meter keypad.
 *
 * Formatted in groups of five because that is how KPLC sends it and how
 * somebody will key it in. A landlord reading an unbroken twenty-digit string
 * off a phone will lose their place, and the meter gives no feedback until the
 * whole thing is wrong.
 */
export const TOKEN_DIGITS = 20;
export function normaliseToken(value) {
    return value.replace(/\D/g, '');
}
export function refuseToken(value) {
    const digits = normaliseToken(value);
    if (digits.length === 0)
        return 'Enter the token from the KPLC message.';
    if (digits.length !== TOKEN_DIGITS) {
        return `A KPLC token is ${TOKEN_DIGITS} digits and that is ${digits.length}. Check the message again — it is usually written in four groups of five.`;
    }
    return null;
}
export function formatToken(value) {
    return normaliseToken(value).replace(/(\d{5})(?=\d)/g, '$1 ').trim();
}
/**
 * Pull the token out of a KPLC SMS.
 *
 * Their message wording changes and there is no API to ask instead, so this
 * looks for the shape rather than the sentence: twenty digits, however they
 * have been grouped. Returns null rather than guessing when the message
 * contains no such run — a wrong token typed into a meter locks it for a
 * period, so a confident wrong answer is worse than no answer.
 */
export function tokenFromMessage(message) {
    // Groups of digits separated by spaces or dashes, totalling twenty.
    const candidates = message.match(/[\d][\d\s-]{18,30}[\d]/g) ?? [];
    for (const candidate of candidates) {
        const digits = normaliseToken(candidate);
        if (digits.length === TOKEN_DIGITS)
            return digits;
    }
    return null;
}
/**
 * Which meter a KPLC message is about.
 *
 * Needed to route a forwarded SMS to the right purchase without a person
 * choosing from a list: the message says which meter it credited, so the
 * system can match it.
 *
 * ## Why the word "meter" is required, and not just eleven digits
 *
 * A token is twenty digits. Hunting for any run of eleven would happily match
 * the first eleven of a token that arrived without spaces — and crediting the
 * wrong landlord is exactly the failure this is supposed to prevent. So the
 * number has to be introduced by the word, which every KPLC message does.
 *
 * The same trailing-full-stop trap that broke `unitsFromMessage` applies here:
 * "for meter 12345678901." must not swallow the dot, so the run is bounded by
 * a digit-count rather than by whatever follows.
 */
export function meterFromMessage(message) {
    /*
     * `String.raw` so the backslashes survive.
     *
     * Written as an ordinary quoted string first, where `'\s'` is not an escape
     * at all — JavaScript drops the backslash and the pattern became `meters*`,
     * matching nothing. It failed silently in exactly the way a regex does: no
     * error, just null for every message.
     *
     * The length comes from METER_DIGITS_MIN/MAX rather than literals, so there
     * is one definition of how long a meter number is.
     */
    /*
     * `Mtr` as well as `meter`, because that is what the message says.
     *
     * A real KPLC token SMS opens `Mtr:54608253347` — no vowels, no space, no
     * word "meter" anywhere in it. This pattern required the full word, so every
     * genuine token message failed to find its own meter number and was refused
     * with "no meter number in the message". It was written against a
     * plausible-looking example rather than a real one, and it took a real one
     * to find out.
     *
     * Both spellings are accepted: KPLC is not the only sender here, and a
     * pattern that stops recognising what it used to is a different bug.
     */
    const pattern = new RegExp(String.raw `(?:meter|mtr)\s*(?:no\.?|number)?\s*:?\s*(\d{${METER_DIGITS_MIN},${METER_DIGITS_MAX}})(?!\d)`, 'i');
    const match = message.match(pattern);
    return match ? match[1] : null;
}
/**
 * Units bought, when KPLC says so.
 *
 * Their message usually carries a kWh figure. Worth capturing because it is
 * the only way to notice that a meter is drawing far more than it should —
 * which usually means somebody has plugged a fridge into the cabinet.
 */
export function unitsFromMessage(message) {
    /*
     * `\d+(\.\d+)?` rather than `[\d.]+`.
     *
     * The loose version matched the *meter number* in a real KPLC message —
     * "for meter 12345678901. Units 45.20 kWh" — because `[\d.]+` happily
     * swallowed the trailing full stop and `\s*` then reached "Units". That
     * produced 12,345,678,901 units, which overflowed the column and turned a
     * successful token into a 500.
     *
     * Requiring digits after any decimal point stops the sentence-ending dot
     * being read as part of the number.
     */
    /*
     * The number can come before the word or after it.
     *
     * "45.20 kWh" is the shape this was written for. A real KPLC message says
     * `Units:14.5` — label first, colon, then the figure — and against that the
     * original pattern found a number somewhere else entirely and reported it
     * with confidence. It recorded 9 units for a 14.5-unit purchase, which is
     * worse than recording none: a null is obviously missing, and a wrong number
     * is believed.
     *
     * The label-first form is tried first and anchored to the colon or the space
     * straight after the word, so it cannot reach across the message and collect
     * the date.
     */
    const match = message.match(/(?:kwh|units)\s*:?\s*(\d+(?:\.\d+)?)/i) ??
        message.match(/(\d+(?:\.\d+)?)\s*(?:kwh|units)\b/i);
    if (!match)
        return null;
    const units = Number(match[1]);
    if (!Number.isFinite(units) || units <= 0)
        return null;
    /*
     * A sanity ceiling. A prepaid top-up is tens or hundreds of units; anything
     * in the thousands is a misparse, and a misparse should be null rather than
     * a confident wrong figure written to the record.
     */
    if (units > 100_000)
        return null;
    return Math.round(units * 100) / 100;
}
export function classifyMessage(text) {
    /*
     * The token wins when both shapes are present.
     *
     * An M-Pesa confirmation never contains twenty consecutive digits, so a
     * message that has one is the KPLC message — even if it also quotes the
     * receipt, which KPLC's messages sometimes do. Checking this first means a
     * message carrying both is read as the thing that actually matters.
     */
    if (tokenFromMessage(text) !== null)
        return 'token';
    if (receiptFromMessage(text) !== null && amountFromMessage(text) !== null)
        return 'payment';
    return 'unknown';
}
/**
 * The M-Pesa receipt code.
 *
 * Ten characters, letters and digits, upper case — "SFF4H8K2L1". Safaricom
 * puts it at the very start of a confirmation and KPLC quotes it back, which
 * is what lets the two messages be tied together.
 *
 * ## The trap
 *
 * A bare `[A-Z0-9]{10}` matches ten characters of any twenty-digit token, and
 * matches parts of ordinary words in capitals. So it is anchored to the start
 * of the message or to the word "receipt", requires at least one letter and at
 * least one digit — every real receipt has both — and is bounded so it cannot
 * bite a chunk out of a longer run.
 */
export function receiptFromMessage(text) {
    const patterns = [
        /(?:^|\n)\s*([A-Z0-9]{10})\b/,
        /receipt\s*(?:no\.?|number)?\s*:?\s*([A-Z0-9]{10})\b/i,
    ];
    for (const pattern of patterns) {
        const match = text.match(pattern);
        const candidate = match?.[1]?.toUpperCase();
        if (!candidate)
            continue;
        if (!/[A-Z]/.test(candidate))
            continue;
        if (!/\d/.test(candidate))
            continue;
        return candidate;
    }
    return null;
}
/**
 * The money, in cents.
 *
 * "Ksh1,500.00", "KES 1,500", "Ksh 1500.50" — all of them, because Safaricom
 * and KPLC write it differently and both have changed it before.
 *
 * Returned in cents rather than shillings so it can be compared directly with
 * `amountCents` on the row. Going through a float and back is how a 1,500.00
 * becomes a 149,999.
 */
export function amountFromMessage(text) {
    const match = text.match(/\b(?:ksh|kes|kshs)\.?\s*([\d,]+(?:\.\d{1,2})?)/i);
    if (!match)
        return null;
    const figure = Number(match[1].replace(/,/g, ''));
    if (!Number.isFinite(figure) || figure <= 0)
        return null;
    return Math.round(figure * 100);
}
/**
 * The account the money was paid to, which for KPLC prepaid *is* the meter.
 *
 * Safaricom writes "for account 12345678901". Requiring the word means a
 * confirmation that happens to contain eleven digits somewhere else — a phone
 * number, most obviously — cannot be read as a meter number and credited to
 * the wrong landlord.
 */
export function accountFromMessage(text) {
    const pattern = new RegExp(String.raw `(?:account|acc|a/c)\s*(?:no\.?|number)?\s*:?\s*(\d{${METER_DIGITS_MIN},${METER_DIGITS_MAX}})(?!\d)`, 'i');
    const match = text.match(pattern);
    return match ? match[1] : null;
}
/* ── Making the two messages prove each other ────────────────────────────── */
/**
 * How long after the payment a token is still believable.
 *
 * KPLC answers in seconds when it answers at all, and in an hour when their
 * platform is struggling. Six hours is generous enough that a real token is
 * never refused and tight enough that a message forwarded from last month
 * cannot be attached to this month's purchase — which is the specific way this
 * goes wrong, because the handset holds every token it has ever received and a
 * forwarder set up today will happily send the lot.
 */
export const TOKEN_WINDOW_HOURS = 6;
/**
 * Whether this token belongs to this purchase, said as a sentence or not at
 * all.
 *
 * Every rule here exists because the alternative is sending a stranger's
 * twenty digits to a landlord, who keys them into a meter and locks it for a
 * period. None of these refusals is recoverable by being clever afterwards, so
 * all of them happen before anything is sent.
 *
 * Returns null when the token is good. A sentence otherwise, written for
 * whoever has to go and look at it.
 */
export function refuseForwardedToken(forwarded, purchase) {
    const shape = refuseToken(forwarded.token);
    if (shape)
        return shape;
    /*
     * Nothing that predates the purchase.
     *
     * The single most likely bad match, and the reason this function exists: a
     * forwarder installed on a handset that has been buying tokens for a year
     * starts by sending the backlog, and every one of those messages names a
     * real meter and carries a real token.
     */
    if (forwarded.receivedAt.getTime() < purchase.requestedAt.getTime()) {
        return 'that message arrived before this purchase was raised, so it belongs to an earlier one';
    }
    /*
     * And nothing that arrives long after it. Measured from the payment when we
     * know it, because the gap that matters is KPLC's answer time and not how
     * long the purchase sat waiting for somebody to go and pay.
     */
    const since = purchase.paidAt ?? purchase.requestedAt;
    const hours = (forwarded.receivedAt.getTime() - since.getTime()) / 3_600_000;
    if (hours > TOKEN_WINDOW_HOURS) {
        return `that message arrived ${Math.round(hours)} hours after the payment, which is too long to be its token`;
    }
    /*
     * The money has to agree. A token for KES 500 against a purchase of KES
     * 1,500 is either the wrong message or a payment that went somewhere else,
     * and both want a person rather than a guess.
     */
    if (forwarded.amountCents !== null && forwarded.amountCents !== purchase.amountCents) {
        return `that message is for ${formatCents(forwarded.amountCents)} and this purchase was ${formatCents(purchase.amountCents)}`;
    }
    /*
     * The receipt ties the two messages together, and is only checked when both
     * halves are known. A KPLC message that quotes no receipt is not suspicious
     * — plenty do not — but one that quotes a *different* one is a different
     * payment.
     */
    if (forwarded.receipt !== null &&
        purchase.paymentRef !== null &&
        forwarded.receipt !== purchase.paymentRef) {
        return `that message quotes receipt ${forwarded.receipt} and this purchase was paid with ${purchase.paymentRef}`;
    }
    return null;
}
/**
 * When KPLC says it issued the token — the "Date:20261016 07:46" line.
 *
 * Kenya time, which is UTC+3 all year. Worth more than the moment the message
 * reached the office: a token forwarded by hand four hours after it arrived
 * was still issued a minute after the payment, and that minute is what the
 * timing rule is about.
 */
export function issuedAtFromMessage(text) {
    const match = /Date:\s*(\d{4})(\d{2})(\d{2})\s+(\d{1,2}):(\d{2})/i.exec(text);
    if (!match)
        return null;
    const [, y, mo, d, h, mi] = match.map(Number);
    const when = new Date(Date.UTC(y, mo - 1, d, h - 3, mi));
    return Number.isNaN(when.getTime()) ? null : when;
}
/**
 * Which waiting purchase a forwarded token belongs to, if any.
 *
 * Paid ones first, newest first, and the first that the token actually fits.
 * Asking the database for "paid before pending" with an enum sort gave the
 * opposite — Postgres orders an enum by declaration, and `pending` is declared
 * first — so a purchase abandoned yesterday was picked over the one paid a
 * minute ago, the token was judged "24 hours late" against it, and the
 * landlord got nothing.
 *
 * Trying the next candidate after a refusal does not loosen anything: each one
 * is held to every rule in `refuseForwardedToken`. When none fits, the refusal
 * reported is the one for the most likely purchase, because that is the one a
 * person will go and look at.
 */
export function chooseTokenPurchase(forwarded, waiting) {
    const ordered = [...waiting].sort((a, b) => {
        if (a.status !== b.status)
            return a.status === 'paid' ? -1 : 1;
        return b.requestedAt.getTime() - a.requestedAt.getTime();
    });
    let first = null;
    for (const purchase of ordered) {
        const refusal = refuseForwardedToken(forwarded, purchase);
        if (!refusal)
            return { purchase, refusal: null };
        first ??= { purchase, refusal };
    }
    return first ?? { purchase: null, refusal: 'nothing is waiting for a token on that meter' };
}
/* ── Buying ──────────────────────────────────────────────────────────────── */
/**
 * KPLC's own paybill. Not configurable, because it is not ours to change and
 * a typo here sends everybody's money somewhere else.
 */
export const KPLC_PAYBILL = '888880';
/**
 * The least KPLC will sell.
 *
 * Below this the payment is accepted and no token is issued, which is the
 * worst possible outcome: money gone, no power, and nothing that looks like an
 * error anywhere.
 */
export const MIN_PURCHASE_KES = 50;
export const MAX_PURCHASE_KES = 35_000;
export const PURCHASE_STATUSES = [
    'pending',
    'paid',
    'delivered',
    'failed',
];
export const PURCHASE_STATUS_LABEL = {
    pending: 'Paying',
    paid: 'Paid, waiting for the token',
    delivered: 'Token sent to the landlord',
    failed: 'Did not go through',
};
/**
 * Why this meter cannot be paid for.
 *
 * Every one of these is caught before money moves, because none of them is
 * recoverable afterwards: a token bought against a mistyped meter number
 * belongs to a stranger, and a token that cannot be sent anywhere is a token
 * nobody can use.
 */
export function refusePurchase(meter, amountCents) {
    if (!meter.isActive) {
        return { field: 'meter', message: `${meter.label} is switched off in the meter list.` };
    }
    const meterProblem = refuseMeterNumber(meter.meterNumber);
    if (meterProblem)
        return { field: 'meterNumber', message: meterProblem };
    const shillings = Math.round(amountCents / 100);
    if (shillings < MIN_PURCHASE_KES) {
        return {
            field: 'amount',
            message: `KPLC will not sell less than KES ${MIN_PURCHASE_KES}. Anything under that is taken and no token comes back.`,
        };
    }
    if (shillings > MAX_PURCHASE_KES) {
        return {
            field: 'amount',
            message: `KES ${shillings.toLocaleString('en-KE')} is more than a single prepaid purchase allows. Split it, or check the amount.`,
        };
    }
    /*
     * The token has to reach somebody. A meter with no phone number is a meter
     * whose token would sit in this system unread while the power runs out.
     */
    try {
        toE164(meter.landlordPhone);
    }
    catch {
        return {
            field: 'landlordPhone',
            message: `${meter.label} has no usable phone number for the landlord, so there is nowhere to send the token.`,
        };
    }
    return null;
}
/**
 * What pressing the button would actually do, before it does it.
 *
 * Deliberately a separate step from paying. Forty meters is a five-figure sum
 * leaving the account in one action, and a screen that says "pay 38 meters,
 * KES 47,500, two refused because their meter numbers are wrong" is the
 * difference between a considered decision and a click.
 *
 * Refused lines are returned rather than silently dropped. A meter that cannot
 * be paid for is exactly the one somebody needs to know about — it is the one
 * whose power will run out.
 */
export function planBatch(meters, options = {}) {
    /*
     * The amount is what was last actually paid for the meter, not a budget
     * typed in once and never checked against what the landlord's power costs.
     * A meter never paid through this system has no amount to repeat, so it is
     * refused with that reason rather than paid an invented sum.
     */
    const amountFor = options.amountForCents ?? ((meter) => meter.lastPaidCents ?? 0);
    const lines = meters.map((meter) => {
        const amountCents = amountFor(meter);
        const unknown = !options.amountForCents && (meter.lastPaidCents === null || meter.lastPaidCents === undefined) && meter.isActive
            ? {
                field: 'amount',
                message: `${meter.label} has never been paid for through this system, so there is no amount to repeat. Pay it once from the phone with the amount the landlord needs.`,
            }
            : null;
        return { meter, amountCents, refusal: unknown ?? refusePurchase(meter, amountCents) };
    });
    const payable = lines.filter((line) => !line.refusal);
    const refused = lines.filter((line) => line.refusal);
    const totalCents = payable.reduce((sum, line) => sum + line.amountCents, 0);
    const money = `KES ${Math.round(totalCents / 100).toLocaleString('en-KE')}`;
    const summary = payable.length === 0
        ? refused.length === 0
            ? 'There are no meters to pay for.'
            : `Nothing can be paid — all ${refused.length} meters have something wrong with them.`
        : refused.length === 0
            ? `${payable.length} meter${payable.length === 1 ? '' : 's'}, ${money} in total.`
            : `${payable.length} meter${payable.length === 1 ? '' : 's'}, ${money} in total. ${refused.length} cannot be paid and ${refused.length === 1 ? 'is' : 'are'} listed below.`;
    return { payable, refused, totalCents, summary };
}
/** Kenya is UTC+3 all year: the calendar a payment date is read in. */
const NAIROBI_OFFSET_MS = 3 * 3_600_000;
/**
 * When the next monthly payment falls due: the same date next month, in
 * Nairobi. Paid on 12 October, due on 12 November. A date the next month does
 * not have becomes its last day: paid 31 January, due 28 (or 29) February.
 * Returned as the start of that day in Nairobi.
 */
export function nextDueDate(paidAt) {
    const local = new Date(paidAt.getTime() + NAIROBI_OFFSET_MS);
    const year = local.getUTCFullYear();
    const month = local.getUTCMonth() + 1;
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const day = Math.min(local.getUTCDate(), lastDay);
    return new Date(Date.UTC(year, month, day) - NAIROBI_OFFSET_MS);
}
/** "12 Nov 2026", the way a due date is shown. */
export function formatDueDate(date) {
    return new Date(date.getTime() + NAIROBI_OFFSET_MS).toLocaleDateString('en-KE', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    });
}
export function dueState(lastPaidAt, asOf, soonDays = 3) {
    if (!lastPaidAt)
        return 'never';
    const due = nextDueDate(lastPaidAt).getTime();
    const startOfToday = startOfNairobiDay(asOf).getTime();
    if (due < startOfToday)
        return 'overdue';
    if (due === startOfToday)
        return 'due_today';
    if (due - startOfToday <= soonDays * 86_400_000)
        return 'due_soon';
    return 'paid';
}
function startOfNairobiDay(at) {
    const local = new Date(at.getTime() + NAIROBI_OFFSET_MS);
    return new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate()) - NAIROBI_OFFSET_MS);
}
/**
 * Which meters are worth topping up now.
 *
 * There is no way to read a prepaid meter's balance remotely without KPLC's
 * cooperation, so "due" follows the monthly payment: a meter is due on the
 * same date the month after it was last paid for (nextDueDate), and a meter
 * never paid for is due now. The screen says when it was paid and when it is
 * due, not "low balance".
 */
export function dueForTopUp(meters, asOf) {
    return meters
        .filter((meter) => meter.isActive)
        .filter((meter) => {
        if (!meter.lastPurchasedAt)
            return true;
        return nextDueDate(meter.lastPurchasedAt).getTime() <= asOf.getTime();
    })
        .sort((a, b) => {
        // Longest since a top-up first — the one closest to going dark.
        const left = a.lastPurchasedAt?.getTime() ?? 0;
        const right = b.lastPurchasedAt?.getTime() ?? 0;
        return left - right;
    });
}
/** How long since this meter was last topped up, in words. */
export function sinceTopUp(meter, asOf) {
    if (!meter.lastPurchasedAt)
        return 'never topped up through this system';
    const days = Math.floor((asOf.getTime() - meter.lastPurchasedAt.getTime()) / 86_400_000);
    if (days === 0)
        return 'topped up today';
    if (days === 1)
        return 'topped up yesterday';
    return `topped up ${days} days ago`;
}
/* ── Telling the landlord ────────────────────────────────────────────────── */
/**
 * The message that goes to the landlord.
 *
 * Written to be read on a basic handset and keyed straight into a meter, which
 * rules out most of what a message template usually contains. No greeting
 * three lines long, no reference number they do not need, and the token in its
 * five-digit groups on a line of its own so it can be read without losing
 * one's place.
 *
 * GoSpeedNet is named because a twenty-digit number arriving from an unknown
 * number looks exactly like a scam, and a landlord who deletes it is a FAT box
 * that goes dark.
 */
/* ── The prompt that reaches the paying phone ────────────────────────────── */
/**
 * Why pressing Pay cannot put an M-Pesa PIN prompt on the handset by itself.
 *
 * Safaricom's STK push — the prompt that appears with the amount already in it
 * and asks only for a PIN — is initiated by the business being *paid*. The
 * request carries `BusinessShortCode` and a password derived from that
 * shortcode's passkey, and `PartyB` is the same shortcode: the money can only
 * land in the till whose credentials signed the request.
 *
 * Paybill 888880 is Kenya Power's. Their passkey is theirs, so no third party
 * can raise a prompt that credits it, and this system is a third party. The
 * `MpesaProvider` in this repository can push all day — into GoSpeedNet's own
 * shortcode, which is collecting money, not paying KPLC.
 *
 * So the next best thing is done properly rather than the impossible thing
 * being faked: the moment Pay is pressed, everything needed to make the
 * payment is sent to the handset that is going to make it. The paybill, the
 * account number, the amount, and — when the organisation has set one — a
 * tap-to-dial USSD shortcut that walks the M-Pesa menu with those values
 * already filled in, so that the only thing left to type is the PIN.
 *
 * The one genuine route to a single prompt is B2B: GoSpeedNet's own shortcode
 * paying KPLC's, which needs an initiator credential and a Safaricom approval
 * applied for separately.
 */
/**
 * The USSD chain that opens M-Pesa with the paybill and amount already in it.
 *
 * ## Why this is a template in settings rather than a constant here
 *
 * Because the menu numbers are Safaricom's and they move. `*334#` opens
 * M-Pesa, and the positions of "Lipa na M-Pesa" and "Pay Bill" underneath it
 * differ between account types and have been renumbered more than once. A
 * wrong chain does not fail safely — it walks into a *different* menu with a
 * paybill and an amount behind it.
 *
 * So nothing is guessed. The suggestion below is what to try first; whoever
 * holds the paying handset dials it once, sees where it lands, and saves the
 * chain that works. From then on the SMS carries a link that needs one tap and
 * a PIN.
 */
export const SUGGESTED_USSD_TEMPLATE = '*334*6*1*{paybill}*{account}*{amount}#';
/**
 * The menu, as a real Safaricom line actually shows it.
 *
 * Read off a recording of the handset rather than guessed. The guess had
 * `4*2*1`, and `4` is "Loans and Savings" — so every attempt walked into the
 * wrong menu and sat there until the session timed out. The numbers below are
 * what the phone prints.
 *
 * Kept here rather than in the screen that draws it, because the suggested
 * template above has to agree with it and the two drifting apart is the whole
 * failure happening again.
 */
export const USSD_WALKTHROUGH = [
    { press: '*334#', shows: 'The M-PESA menu opens.' },
    { press: '6', shows: 'Lipa na M-PESA' },
    { press: '1', shows: 'Pay Bill' },
    { press: '{paybill}', shows: 'Enter Business Number' },
    { press: '{account}', shows: 'Enter Account Number' },
    { press: '{amount}', shows: 'Enter Amount' },
    { press: 'your PIN', shows: 'Enter M-PESA PIN' },
];
/**
 * The macro that pays, derived from the menu rather than typed out again.
 *
 * ## Why it is unrolled rather than a loop
 *
 * MacroDroid can loop over an array, and building that turned out far harder
 * than five plain blocks — the For Each route dead-ends in array-index
 * dialogs. Five repetitions of paste / click / wait is more lines and less
 * work, and anybody reading the macro can see what it does.
 *
 * ## Why it is derived from `USSD_WALKTHROUGH`
 *
 * Because the two must agree and a copy would not stay agreed. The
 * walkthrough is what the handset prints and the macro is what answers it, so
 * every reply here is one prompt there, in the same order — and if somebody
 * corrects a menu number in one place the other follows on its own. The first
 * version of that menu path was wrong at step one and cost a day; a second
 * copy of it to keep in sync would be asking for the same day again.
 *
 * The PIN is deliberately absent, and never automated. It is the one thing
 * standing between a macro with a bug and somebody's money.
 */
export function dialMacroActions() {
    /* Everything the menu asks for after the service code and before the PIN. */
    const replies = USSD_WALKTHROUGH.slice(1, -1);
    const actions = [
        {
            step: 1,
            kind: 'Popup Message',
            detail: 'Pay KES {v=amount} - {v=meter}, acct {v=account}',
            because: 'So the person can see which meter is about to be paid before the menu takes over the screen.',
        },
        {
            step: 2,
            kind: 'Call',
            detail: USSD_WALKTHROUGH[0].press,
            because: 'Typed literally, not from a variable — this one never changes.',
        },
        {
            step: 3,
            kind: 'Wait',
            detail: '3 seconds',
            because: 'The first menu is the slowest to arrive. Everything after it waits two.',
        },
    ];
    for (const reply of replies) {
        const value = reply.press.startsWith('{')
            ? `{v=${reply.press.slice(1, -1)}}`
            : reply.press;
        actions.push({
            step: actions.length + 1,
            kind: 'UI Interaction → Paste',
            detail: `Custom text: ${value}`,
            because: `Answers "${reply.shows}".`,
        });
        actions.push({
            step: actions.length + 1,
            kind: 'UI Interaction → Click',
            detail: 'Text content contains SEND',
            because: null,
        });
        actions.push({
            step: actions.length + 1,
            kind: 'Wait',
            detail: '2 seconds',
            because: null,
        });
    }
    return actions;
}
/**
 * The variables the unrolled macro needs, and nothing more.
 *
 * Four. `ussd`, `steps` and `menuPath` are still sent and still documented,
 * but they are for a macro that loops over an array — listing them as required
 * sends somebody declaring three variables that will never be read.
 */
export const MACRO_VARIABLES = ['amount', 'meter', 'account', 'paybill'];
/**
 * The macro that delivers, for the route that needs no vendor at all.
 *
 * ## Why this route exists
 *
 * Every SMS gateway is a queue somebody else controls: an account to open, a
 * sender ID to have approved, an address to have allowlisted. Each of those is
 * a support ticket, and a support ticket is not a plan.
 *
 * Meanwhile the token lands on a handset that already runs a macro, already
 * answers a webhook, and already sends this system its messages. It can send
 * one more. No account, no sender ID, no allowlist — just the SIM's own
 * tariff.
 *
 * ## The two things that are got wrong
 *
 * The trigger must be **Webhook (URL)**, not anything that watches for a
 * message. A macro triggered by an arriving SMS would send the token to
 * whoever last texted, which is the sort of mistake that only shows up on
 * somebody else's phone.
 *
 * And the number must come from the webhook, not from the phone's own idea of
 * who it was talking to. `to` and `text` arrive as query parameters; MacroDroid
 * exposes them as variables of the same name, and both must be declared as
 * **String** before the macro will save them.
 */
export const SEND_MACRO_ACTIONS = [
    {
        step: 1,
        kind: 'Popup Message',
        detail: 'Trigger: search the trigger list for "webhook" → Webhook (URL) — the same one the ' +
            'dialling macro uses. Set the identifier to send-token, then copy the ' +
            'whole address it shows — both parts, the long number AND the word after it.',
        because: 'The word after the last slash is the trigger’s own name. Typing a different one ' +
            'gets {"error":"Not found"}, which reads like a broken macro and is not.',
    },
    {
        step: 2,
        kind: 'Popup Message',
        detail: 'Variables: add "to" and "text", both of type String.',
        because: 'MacroDroid drops a parameter it has no variable for, silently.',
    },
    {
        step: 3,
        kind: 'Popup Message',
        detail: 'Action: search for "send sms" → Send SMS. For the number choose Variable → to, and in ' +
            'the message body insert the variable text rather than typing it.',
        because: 'Inserted from the variable picker, not typed. A typed name is sent as those letters, ' +
            'and the landlord receives the word "text".',
    },
    {
        step: 4,
        kind: 'Popup Message',
        detail: 'In that action, turn OFF "Show notification" and leave "Pre-populate" unticked.',
        because: 'A pre-populated message waits for somebody to press send, and nothing arrives.',
    },
    {
        step: 5,
        kind: 'Popup Message',
        detail: 'Constraint (optional): Battery → Not charging is NOT wanted. Leave constraints empty.',
        because: 'A constraint that is not met makes the macro do nothing and report success.',
    },
];
/** Sent on every send trigger. Both must exist, and both must be String. */
export const SEND_MACRO_VARIABLES = ['to', 'text'];
/** Sent on every trigger, and only needed by a looping macro. */
export const MACRO_LOOP_VARIABLES = ['steps', 'menuPath', 'ussd', 'ussdEncoded'];
/** What a template may contain, so a mistyped one is caught before it dials. */
export const USSD_PLACEHOLDERS = ['{paybill}', '{account}', '{amount}'];
/**
 * Refuse a USSD template that could not work, in words that say what to fix.
 *
 * Checked when it is saved rather than when it is dialled. A template is typed
 * once and used on every payment afterwards, so the cost of catching a mistake
 * late is every payment until somebody notices.
 */
export function refuseUssdTemplate(template) {
    const trimmed = template.trim();
    if (trimmed.length === 0)
        return null;
    if (!trimmed.startsWith('*'))
        return 'A USSD code starts with *.';
    if (!trimmed.endsWith('#'))
        return 'A USSD code ends with #.';
    /*
     * The amount is what makes this worth doing at all — a chain without it
     * stops at "enter amount", which is no better than dialling *334# by hand.
     */
    if (!trimmed.includes('{amount}')) {
        return 'Include {amount}, or the shortcut stops and asks for it.';
    }
    if (!trimmed.includes('{paybill}')) {
        return 'Include {paybill}, so the shortcut knows which business to pay.';
    }
    if (!trimmed.includes('{account}')) {
        return 'Include {account} — for KPLC prepaid that is the meter number.';
    }
    const stray = trimmed.match(/\{[a-z]+\}/gi)?.filter((one) => !USSD_PLACEHOLDERS.includes(one.toLowerCase()));
    if (stray && stray.length > 0) {
        return `${stray[0]} is not something this can fill in. Use ${USSD_PLACEHOLDERS.join(', ')}.`;
    }
    if (/[^0-9*#{}a-z]/i.test(trimmed)) {
        return 'A USSD code is only digits, * and #. Remove anything else.';
    }
    return null;
}
/**
 * Fill a template with this payment's own figures.
 *
 * The amount goes in as whole shillings, because that is what the M-Pesa menu
 * accepts — a decimal point typed into it is rejected, and KPLC prepaid is
 * always a round figure anyway.
 *
 * Returns null rather than a half-filled string when there is no template, so
 * a caller cannot accidentally send somebody a dial link made of braces.
 */
export function fillUssd(template, input) {
    if (!template || template.trim().length === 0)
        return null;
    if (refuseUssdTemplate(template))
        return null;
    return template
        .trim()
        .replace('{paybill}', input.paybill)
        .replace('{account}', normaliseMeterNumber(input.account))
        .replace('{amount}', String(Math.round(input.amountCents / 100)));
}
/**
 * What the paying handset is sent the moment somebody presses Pay.
 *
 * ## Why it goes to a phone at all
 *
 * The three facts a payment needs — the paybill, the account number and the
 * amount — were on a screen, and the payment is made on a handset. Somebody
 * was reading one and typing into the other, and the account number is an
 * eleven-digit meter number. A digit misread there pays a stranger's power
 * bill, and the money is gone: M-Pesa has no recall for a paybill.
 *
 * Now the digits arrive on the phone that is going to use them, where they can
 * be copied rather than retyped.
 *
 * ## Why the meter and the landlord are named
 *
 * Because a batch is forty of these, arriving as forty texts, and the account
 * number is the only thing distinguishing them. Saying whose meter it is makes
 * the list on the handset readable — and makes it obvious when one is wrong.
 */
/* ── Poking the paying handset into dialling ─────────────────────────────── */
/**
 * The address on the paying phone that makes it dial.
 *
 * ## Why this exists, given everything else here
 *
 * An STK push cannot be used to pay Kenya Power: Safaricom signs it with the
 * passkey of the shortcode being credited, so it can only ever pay a till you
 * hold credentials for, and 888880 is theirs. B2B would do it and needs an
 * arrangement between two businesses that does not exist here. That looked
 * like the end of the road for "make my phone prompt me".
 *
 * It is not, because the paying handset is already running a macro app for the
 * forwarding half of this. Those apps expose a trigger URL and can dial a USSD
 * code as an action. So the sequence becomes: press Pay here, this calls that
 * URL, the phone dials the chain, and the M-Pesa menu opens on the handset
 * with the paybill, the account and the amount already in it — asking for a
 * PIN and nothing else.
 *
 * Which is the thing that was wanted, reached the other way round: the prompt
 * is raised *by* the phone rather than sent *to* it.
 *
 * ## What is refused, and why each one
 *
 * The URL is typed once and then called on every payment, so a mistake here is
 * a mistake repeated. And it is called by a server with the payment details
 * attached, which makes two of these about more than tidiness.
 */
export function refuseDialWebhook(url) {
    return refuseWebhook(url, 'the meter number and amount');
}
/**
 * The address on the paying phone that makes it send an SMS.
 *
 * Identical rules to the dialling trigger, and deliberately so: both are a URL
 * a person types once and a server then fetches on their behalf, which is the
 * shape of a server-side request forgery whatever the phone does at the other
 * end. What travels differs — a token rather than a meter number — and a token
 * is a bearer credential for somebody's electricity, so if anything the case
 * for https is stronger here.
 */
export function refuseSendWebhook(url) {
    return refuseWebhook(url, "the landlord's number and the token");
}
/**
 * Shared, rather than written out twice.
 *
 * Two triggers with the same threat model and two copies of the rules is how
 * one of them quietly stops matching the other. `carries` names what is in the
 * request, because "why does this have to be https" is answered by what would
 * otherwise be readable.
 */
function refuseWebhook(url, carries) {
    const trimmed = url.trim();
    if (trimmed.length === 0)
        return null;
    let parsed;
    try {
        parsed = new URL(trimmed);
    }
    catch {
        return 'That is not a web address. Paste the whole trigger URL, starting with https://.';
    }
    /*
     * HTTPS only. The meter number and the amount travel in this request, and a
     * plain http:// one puts them in clear text across whatever network the
     * server happens to be on.
     */
    if (parsed.protocol !== 'https:') {
        return `The trigger address has to start with https:// — ${carries} travel in it.`;
    }
    /*
     * Not our own host, and not a private address.
     *
     * This is a URL a person types and a server then fetches, which is the shape
     * of a server-side request forgery: point it at localhost or at an internal
     * address and the server will happily fetch things nobody outside could
     * reach. The trigger lives on the public internet, so anything that does not
     * is a mistake at best.
     */
    const host = parsed.hostname.toLowerCase();
    const isPrivate = host === 'localhost' ||
        host === '::1' ||
        host.endsWith('.local') ||
        host.endsWith('.internal') ||
        /^127\./.test(host) ||
        /^10\./.test(host) ||
        /^192\.168\./.test(host) ||
        /^169\.254\./.test(host) ||
        /^172\.(1[6-9]|2\d|3[01])\./.test(host);
    if (isPrivate) {
        return 'That address is on a private network. The trigger has to be one the internet can reach, which is what the macro app gives you.';
    }
    return null;
}
/**
 * The trigger URL with this payment's own figures hung off it.
 *
 * Query parameters rather than a JSON body, because that is what the macro
 * apps read: each one arrives on the phone as a variable the macro can put
 * straight into a dial action. `ussd` is the whole point; the rest is there so
 * the macro can show a notification saying which meter it is about to pay.
 *
 * Returns null when there is nothing to dial. A trigger fired without a USSD
 * chain would wake the phone up to do nothing.
 */
/**
 * The menu digits from a chain, without the figures.
 *
 * `*334*4*2*1*888880*12345678901*1500#` is four menu answers and three
 * values. The values are already sent separately, so this pulls out the
 * answers: everything between the service code and the paybill.
 *
 * Positional rather than clever, because there is nothing in the string that
 * marks where the menu stops and the payment starts — the paybill is simply
 * the first value, and a template that named it as `{paybill}` is how we know
 * which one that is.
 */
export function ussdMenuPath(ussd) {
    const parts = ussd.replace(/^\*/, '').replace(/#$/, '').split('*');
    if (parts.length <= 4)
        return [];
    /*
     * Drop the service code at the front and the three values at the back. What
     * is left is the path through the menu, in order.
     */
    return parts.slice(1, parts.length - 3);
}
/**
 * The send trigger with this token hung off it.
 *
 * ## Why the message goes whole rather than in pieces
 *
 * The dialling trigger sends the paybill, the account and the amount
 * separately, because the macro has to type each into a different field of the
 * M-Pesa menu. Nothing like that applies here: the handset has one job, which
 * is to put these exact words in front of the landlord. Sending the words
 * already written means the wording lives in one place — `tokenMessage()`,
 * where the Kiswahili version is — rather than being reassembled on a phone by
 * somebody editing a macro.
 *
 * ## Why `searchParams`
 *
 * It encodes a space as `+`, which is right for a form and wrong for a phone
 * taking the value literally. But this message has newlines in it, and those
 * must survive — so the encoding is done properly and the `+` is corrected
 * afterwards, rather than hand-rolling an encoder that gets the newlines wrong
 * instead. A token that arrives as one run-on line is unreadable; a token that
 * arrives with `+` between every word is unusable.
 *
 * Returns null when there is nothing to send to, or nothing to say. A trigger
 * fired without either wakes the phone up to text nobody.
 */
export function sendWebhookFor(url, input) {
    if (!url || url.trim().length === 0)
        return null;
    if (refuseSendWebhook(url))
        return null;
    const to = input.to.trim();
    const message = input.message.trim();
    if (to.length === 0 || message.length === 0)
        return null;
    const target = new URL(url.trim());
    target.searchParams.set('to', to);
    target.searchParams.set('message', message);
    /*
     * `+` back to `%20`, in the query only.
     *
     * `searchParams` writes spaces the form way. The handset reads the value as
     * the literal text of the SMS, so every space would arrive as a plus sign
     * and the landlord would be sent something they cannot read. A real plus —
     * the one in front of a phone number — is already `%2B` by this point, so
     * this cannot corrupt it.
     */
    target.search = target.search.replace(/\+/g, '%20');
    return target.toString();
}
export function dialWebhookFor(url, input) {
    if (!url || url.trim().length === 0)
        return null;
    if (refuseDialWebhook(url))
        return null;
    if (!input.ussd)
        return null;
    const target = new URL(url.trim());
    target.searchParams.set('ussd', input.ussd);
    /*
     * The same chain with the hash escaped, because Android eats it.
     *
     * A USSD code ends in `#`, and a `tel:` intent treats `#` as the start of a
     * URI fragment — so the dialler is handed `*334*...*1500` with the terminator
     * silently removed, and the code never executes. It is the classic Android
     * USSD bug, and it fails in the most confusing possible way: the dialler
     * opens, the digits are right, and nothing happens.
     *
     * `%23` is the fix, and which of the two a given macro app needs depends on
     * whether it encodes the value itself — so both are sent and the macro can
     * use whichever works, rather than somebody spending an evening on it.
     */
    target.searchParams.set('ussdEncoded', input.ussd.replace(/#/g, '%23'));
    target.searchParams.set('paybill', input.paybill);
    target.searchParams.set('account', normaliseMeterNumber(input.account));
    target.searchParams.set('amount', String(Math.round(input.amountCents / 100)));
    target.searchParams.set('meter', input.meter);
    target.searchParams.set('landlord', input.landlord);
    /*
     * The menu digits on their own, for a macro that walks the menu rather than
     * trying to send it all at once.
     *
     * ## Why this had to exist
     *
     * Because the one-shot chain does not work, and two attempts on a real
     * handset said so the same way both times: Safaricom opened the session and
     * then answered "Session timeout". M-PESA's menus are interactive — each
     * level waits for a reply *within* the session — so a string carrying every
     * answer at once satisfies the first prompt and leaves the rest unanswered
     * until the session dies.
     *
     * A macro that sends `*334#`, reads the reply, sends `4`, reads, sends `2`,
     * and so on is answering the menu the way the menu expects. Every value it
     * needs is already here; what was missing was the path, split from the
     * figures so it can be replayed one step at a time.
     */
    const path = ussdMenuPath(input.ussd);
    if (path.length > 0)
        target.searchParams.set('menuPath', path.join(','));
    target.searchParams.set('steps', [...path, input.paybill, normaliseMeterNumber(input.account), String(Math.round(input.amountCents / 100))].join(','));
    return target.toString();
}
export function payPromptMessage(input) {
    const company = input.companyName ?? 'GoSpeedNet';
    const paybill = input.paybill ?? KPLC_PAYBILL;
    const amount = `KES ${Math.round(input.amountCents / 100).toLocaleString('en-KE')}`;
    return [
        `${company}: power for ${input.meterLabel} (${input.landlordName}).`,
        '',
        `Paybill: ${paybill}`,
        `Account: ${normaliseMeterNumber(input.meterNumber)}`,
        `Amount: ${amount}`,
        ...(input.ussd ? ['', `Tap to pay: ${input.ussd}`, 'Then enter your M-Pesa PIN.'] : []),
        '',
        'The KPLC token comes back to this phone and is forwarded automatically.',
    ].join('\n');
}
/**
 * The languages a token can be sent in.
 *
 * Two, deliberately. Every additional one is a translation nobody in the
 * office can check, and a token message that reads oddly is worse than one in
 * a second language the reader half-knows — they act on the digits either way,
 * but only if they trust the message enough to open it.
 */
export const TOKEN_LANGUAGES = [
    { value: 'en', label: 'English' },
    { value: 'sw', label: 'Kiswahili' },
];
export function isTokenLanguage(value) {
    return TOKEN_LANGUAGES.some((one) => one.value === value);
}
export function tokenMessage(input) {
    const company = input.companyName ?? 'GoSpeedNet';
    const firstName = input.landlordName.trim().split(/\s+/)[0] ?? input.landlordName;
    const amount = input.amountCents === null
        ? null
        : `KES ${Math.round(input.amountCents / 100).toLocaleString('en-KE')}`;
    const swahili = input.language === 'sw';
    /* Whichever of the two facts we actually have, and nothing invented. */
    const detail = swahili
        ? input.units && amount
            ? `Yuniti ${input.units}, ${amount}.`
            : input.units
                ? `Yuniti ${input.units}.`
                : amount
                    ? `${amount}.`
                    : null
        : input.units && amount
            ? `${input.units} units, ${amount}.`
            : input.units
                ? `${input.units} units.`
                : amount
                    ? `${amount}.`
                    : null;
    if (input.template && input.template.trim().length > 0) {
        return fillSmsTemplate(input.template, {
            '{firstName}': firstName,
            '{landlordName}': input.landlordName,
            '{company}': company,
            '{meterLabel}': input.meterLabel,
            '{meterNumber}': formatMeterNumber(input.meterNumber),
            '{token}': formatToken(input.token),
            '{units}': input.units === null ? null : String(input.units),
            '{amount}': amount,
        });
    }
    return [
        swahili
            ? `${firstName}, ${company} imenunua umeme kwa mita ya ${input.meterLabel}.`
            : `${firstName}, ${company} has bought power for the meter at ${input.meterLabel}.`,
        '',
        `${swahili ? 'Tokeni' : 'Token'}: ${formatToken(input.token)}`,
        `${swahili ? 'Mita' : 'Meter'}: ${formatMeterNumber(input.meterNumber)}`,
        ...(detail ? [detail] : []),
        '',
        swahili
            ? 'Weka tokeni kwenye mita kama kawaida. Ukipata shida, tupigie simu.'
            : 'Key the token into the meter as usual. Any problem, call us.',
    ].join('\n');
}
//# sourceMappingURL=power.js.map